import { loadState } from '../utils/storage'
import { isNil, pickBy } from 'ramda'

export const formInitialState = {
  accId: 0,
  0: {}
}

export const dataInitialState = {
  countries: [],
  cobCountries: [],
  citizenshipCountries: []
}

export const whitelabelInitialState = {
  email: '',
  emailSenderAddress: '',
  loginURL: '',
  logo: '',
  mimeType: '',
  phone: '',
  showDemoButton: false,
  whiteLabelName: '',
  whiteLabelURL: '',
  privacyPolicyURL: '',
  timestamp: 0,
  extFields: [],
  baseFieldsOrder: [],
  customTexts: [],
  error: {},
  targetPlatform: '',
  immediateLoginEnabled: false,
  title: '',
  description: '',
  favicon: '',
  defaultCountry: '',
  mobileVerificationEnabled: false,
  showLabelAsPlaceholder: false,
  moveToUpgradeForm: false,
  redirectToTier2FormAfterCreate: false,
  excludedLanguages: [],
  steps: []
}

export const initialState = {
  form: {
    ...formInitialState
  },
  whitelabel: {
    ...whitelabelInitialState,
    ...loadState('whitelabelConfig')
  },
  validation: {
    tier1: {},
    tier2: {},
    tier: null,
    accId: 0
  },
  user: {},
  data: {
    ...dataInitialState
  },
  flags: {
    isDocumentUploaded: {
      0: false,
      1: false
    }
  },
  additionalUploadedDocs: { primary: [], joint: [], error: {} }
}

export const formReducer = (state, action) => {
  switch (action.type) {
    case 'setExtFieldValue': {
      return {
        ...state,
        [state.accId]: { ...state[state.accId], [action.name]: action.value }
      }
    }
    case 'setAccId':
      return {
        ...state,
        accId: action.accId
      }
    case 'removeFieldValue': {
      return {
        ...state,
        [state.accId]: pickBy((_, key) => key !== action.name)(state[state.accId])
      }
    }
    case 'setIsResendOpen':
      return {
        ...state,
        isResendOpen: action.value
      }
    case 'addAccForm':
      return {
        ...state,
        [action.accNumber]: {}
      }
    case 'clearForm':
      return { ...formInitialState }
    default:
      return state
  }
}

export const whitelabelReducer = (state, action) => {
  switch (action.type) {
    case 'setWL':
      return {
        ...state,
        ...action.whitelabel
      }
    default:
      return state
  }
}

export const dataReducer = (state, action) => {
  switch (action.type) {
    case 'setData':
      return {
        ...state,
        [action.name]: action.data
      }
    case 'setCountries':
      return {
        ...state,
        countries: action.countries
      }
    case 'setCobCountries':
      return {
        ...state,
        cobCountries: action.countries
      }
    case 'setCitizenshipCountries':
      return {
        ...state,
        citizenshipCountries: action.countries
      }
    default:
      return state
  }
}

export const validationReducer = (state, action) => {
  switch (action.type) {
    case 'setValidation':
      return {
        ...state,
        ...action.validation
      }
    case 'setValidationFld': {
      const curTier = `tier${state.tier}`
      return {
        ...state,
        [curTier]: {
          ...state[curTier],
          [state.accId]: {
            ...state[curTier][state.accId],
            [action.name]: action.value
          }
        }
      }
    }
    case 'setTier':
      return {
        ...state,
        tier: action.tier
      }
    case 'addValidationFld': {
      const curTier = `tier${state.tier}`
      const isValidationFldExist = state[`tier${state.tier}`]?.[state.accId]?.[action.name]
      if (!isNil(isValidationFldExist)) {
        return state
      }
      return {
        ...state,
        [curTier]: {
          ...state[curTier],
          [state.accId]: {
            ...state[curTier][state.accId],
            [action.name]: false
          }
        }
      }
    }
    case 'removeValidationFld': {
      const curTier = `tier${state.tier}`
      const newVaidationForForm = pickBy((_, key) => key !== action.name)(state[curTier][state.accId])
      return {
        ...state,
        [curTier]: {
          ...state[curTier],
          [state.accId]: newVaidationForForm
        }
      }
    }
    case 'clearValidation': {
      const newValidation = Object.keys(state[`tier${action.tier}`][state.accId]).reduce((acc, key) => {
        acc[key] = false
        return acc
      }, {})
      return {
        ...state,
        [`tier${action.tier}`]: {
          [state.accId]: newValidation
        }
      }
    }
    case 'setAccId':
      return {
        ...state,
        accId: action.accId
      }
    case 'setTierValidation':
      return {
        ...state,
        [`tier${action.tier}`]: {
          [state.accId]: action.validation
        }
      }
    default:
      return state
  }
}

export const userReducer = (state, action) => {
  switch (action.type) {
    case 'setUser':
      return {
        ...state,
        ...action.user
      }
    case 'clearUser':
      return {}
    default:
      return state
  }
}

export const flagsReducer = (state, action) => {
  switch (action.type) {
    case 'setFlag':
      return {
        ...state,
        [action.name]: action.value
      }
    default:
      return state
  }
}

export const additionalDocUploadReducer = (state, action) => {
  switch (action.type) {
    case 'initDefaultFields': {
      let count = action.count
      const fields = [...Array(count)].map(() => count--)

      return {
        ...state,
        [action.userType]: [...state[action.userType], ...fields]
      }
    }
    case 'initializeFromStorage':
      return {
        ...state,
        ...action.docs
      }
    case 'addField':
      return {
        ...state,
        [action.userType]: [...state[action.userType], action.id]
      }
    case 'addUploadedDoc': {
      const index = state[action.userType].indexOf(action.recIndex)
      const newState = [...state[action.userType]]
      newState[index] = action.id

      return {
        ...state,
        [action.userType]: [...newState]
      }
    }
    case 'addUploadedDocError': {
      return {
        ...state,
        error: { ...state.error, [`${action.userType}${action.id}`]: action.error }
      }
    }
    case 'removeUploadedDoc': {
      const filtered = state[action.userType].filter((id) => id !== action.documentId)

      return {
        ...state,
        [action.userType]: [...filtered]
      }
    }
    default:
      return state
  }
}

export const reducer = ({ form, whitelabel, validation, user, data, flags, additionalDocs }, action) => {
  return {
    form: formReducer(form, action),
    whitelabel: whitelabelReducer(whitelabel, action),
    validation: validationReducer(validation, action),
    user: userReducer(user, action),
    data: dataReducer(data, action),
    flags: flagsReducer(flags, action),
    additionalDocs: additionalDocUploadReducer(additionalDocs, action)
  }
}
